import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'default' | 'liquid-glass';
}

type Coords = {
  top: number;
  left: number;
};

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  delay = 200,
  className = '',
  style,
  variant = 'default',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const effectiveDelay = variant === 'liquid-glass' ? Math.min(delay, 50) : delay;

  useEffect(() => {
    setMounted(true);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const calculateCoords = (): Coords | null => {
    const trigger = triggerRef.current;
    if (!trigger) return null;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();

    const gap = variant === 'liquid-glass' ? 12 : 8;
    const tooltipWidth = tooltipRect?.width ?? 0;
    const tooltipHeight = tooltipRect?.height ?? 0;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipHeight - gap;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.left - tooltipWidth - gap;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.right + gap;
        break;
    }

    const padding = 8;
    const maxLeft = window.innerWidth - tooltipWidth - padding;
    const maxTop = window.innerHeight - tooltipHeight - padding;

    return {
      top: Math.max(padding, Math.min(top, maxTop)),
      left: Math.max(padding, Math.min(left, maxLeft)),
    };
  };

  const updatePosition = () => {
    const nextCoords = calculateCoords();
    if (nextCoords) {
      setCoords(nextCoords);
    }
  };

  const showTooltip = () => {
    if (timeoutId) clearTimeout(timeoutId);
    const id = setTimeout(() => {
      const initialCoords = calculateCoords();
      if (initialCoords) {
        setCoords(initialCoords);
      }
      setIsRendered(true);
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }, effectiveDelay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
    if (variant === 'liquid-glass') {
      setTimeout(() => {
        setIsRendered(false);
      }, 150);
    } else {
      setIsRendered(false);
    }
  };

  useLayoutEffect(() => {
    if (!isRendered) return;

    updatePosition();

    const handleReposition = () => updatePosition();

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isRendered, isVisible, position]);

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-navy-900',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-navy-900',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-navy-900',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-navy-900',
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-flex ${className}`}
        style={style}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>

      {mounted && isRendered && content && coords &&
        createPortal(
          variant === 'liquid-glass' ? (
            <div
              ref={tooltipRef}
              className={`fixed z-[9999] pointer-events-none select-none ${
                isVisible
                  ? 'opacity-100 scale-100 translate-x-0 translate-y-0 transition-[opacity,transform] duration-200 ease-out'
                  : `opacity-0 scale-95 ${
                      position === 'right'
                        ? '-translate-x-1.5'
                        : position === 'left'
                          ? 'translate-x-1.5'
                          : position === 'bottom'
                            ? '-translate-y-1.5'
                            : 'translate-y-1.5'
                    }`
              }`}
              style={{
                top: coords.top,
                left: coords.left,
              }}
              role="tooltip"
            >
              <div className="relative px-3.5 py-1.5 rounded-xl bg-white/75 dark:bg-grey-900/75 backdrop-blur-xl border border-white/50 dark:border-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06),inset_0_1px_0_0_rgba(255,255,255,0.7)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.4),0_2px_6px_rgba(0,0,0,0.2),inset_0_1px_0_0_rgba(255,255,255,0.15)] flex items-center justify-center">
                <span className="text-xs font-semibold tracking-wide text-grey-800 dark:text-grey-100 whitespace-nowrap">
                  {content}
                </span>
              </div>
            </div>
          ) : (
            <div
              ref={tooltipRef}
              className="fixed z-[9999] px-2 py-1 bg-navy-900 text-white text-xs rounded whitespace-nowrap pointer-events-none"
              style={{
                top: coords.top,
                left: coords.left,
              }}
              role="tooltip"
            >
              {content}
              <div className={`absolute border-4 ${arrowClasses[position]}`} />
            </div>
          ),
          document.body
        )}
    </>
  );
};

export default Tooltip;